import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "./Authprovider";
import { SellerStatus } from "@b2b/services";

interface SellerStatusGuardProps {
  children: React.ReactNode;
  allowedStatuses?: SellerStatus[];
}

/** Profile completion threshold (0-100) to access dashboard, analytics, chat. */
const PROFILE_COMPLETION_THRESHOLD = 80;

/**
 * SellerStatusGuard component that restricts access based on seller approval status and 3-step onboarding.
 * - APPROVED: Full access to all seller features
 * - PENDING_APPROVAL / null: Step 1 = only profile_settings + dashboard. Step 2 = + profile, membership_plans (no analytics, chat, storefront). Step 3 = + store_front. After admin approval, full access.
 * - REJECTED/SUSPENDED: No access, show error message
 */
export const SellerStatusGuard: React.FC<SellerStatusGuardProps> = ({
  children,
  allowedStatuses = [SellerStatus.APPROVED],
}) => {
  const {
    sellerStatus,
    userRole,
    isLoading,
    profileCompleteWithoutSocial,
    profileCompletionPercentage,
    hasMembershipPlanSelected,
  } = useAuth();
  const location = useLocation();

  // Only apply guard to seller routes
  if (userRole !== "seller" && userRole !== "admin") {
    return <>{children}</>;
  }

  // Wait for auth to load
  if (isLoading) {
    return <div>Loading...</div>;
  }

  // Admin can always access
  if (userRole === "admin") {
    return <>{children}</>;
  }

  // If user is not a seller, allow access (buyer routes)
  if (userRole !== "seller") {
    return <>{children}</>;
  }

  // APPROVED: full access
  if (sellerStatus === SellerStatus.APPROVED || allowedStatuses.includes(sellerStatus!)) {
    return <>{children}</>;
  }

  if (sellerStatus === SellerStatus.REJECTED) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-red-800 mb-2">
            Account Rejected
          </h2>
          <p className="text-red-600 mb-4">
            Your seller account has been rejected. Please contact support for more information.
          </p>
        </div>
      </div>
    );
  }

  if (sellerStatus === SellerStatus.SUSPENDED) {
    return (
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="max-w-md w-full bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h2 className="text-xl font-semibold text-yellow-800 mb-2">
            Account Suspended
          </h2>
          <p className="text-yellow-600 mb-4">
            Your seller account has been suspended. Please contact support for more information.
          </p>
        </div>
      </div>
    );
  }

  // PENDING_APPROVAL or null: 3-step gating
  // Step 1: only dashboard + profile_settings
  // Step 2: + profile, membership_plans (no analytics, chat, or storefront)
  // Step 3: + store_front (analytics/chat still only after approval)
  const profileComplete =
    (typeof profileCompletionPercentage === "number" && profileCompletionPercentage >= PROFILE_COMPLETION_THRESHOLD) ||
    profileCompleteWithoutSocial === true;
  const limitedPagesStep1Only = ["/seller/profile_settings", "/seller/dashboard"];
  const limitedPagesStep2Unlocked = [
    "/seller/dashboard",
    "/seller/profile",
    "/seller/membership_plans",
    "/seller/profile_settings",
  ];
  const limitedPagesStep3Unlocked = [...limitedPagesStep2Unlocked, "/seller/store_front"];
  const limitedPages = !profileComplete
    ? limitedPagesStep1Only
    : hasMembershipPlanSelected
      ? limitedPagesStep3Unlocked
      : limitedPagesStep2Unlocked;
  const redirectTo = !profileComplete ? "/seller/profile_settings" : "/seller/dashboard";

  if (limitedPages.some((page) => location.pathname.startsWith(page))) {
    return <>{children}</>;
  }
  return <Navigate to={redirectTo} replace />;
};

